import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";
import { agregarSeguimientoTicket } from "../Integracion";
import useGetDataUser from "../../../../utilidades/customHooks/useGetDataUser";

/**
 * Tarjeta con los datos secundarios de la cotización (unidad, fecha, ticket,
 * responsable). Los datos "de un vistazo" (cliente, ruta, tipo, precio) viven
 * en ResumenEjecutivo, justo arriba.
 */
const TarjetaInfoGeneral = ({ cotizacion, cambiosRuta = [], setCotizacion }) => {
    const [showTicketDetail, setShowTicketDetail] = useState(false);
    const [nuevaObservacion, setNuevaObservacion] = useState("");
    const [enviandoObservacion, setEnviandoObservacion] = useState(false);
    const cambioUnidad = cambiosRuta.find(c => c.campo === "Tipo de unidad");

    const user = useGetDataUser();

    const formatearFecha = (fecha) => {
        if (!fecha) return "Sin fecha";
        // "YYYY-MM-DD" se toma como fecha local para no recorrer un día
        const date = fecha.length === 10 ? new Date(`${fecha}T00:00:00`) : new Date(fecha);
        return date.toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    };

    const formatearFechaHora = (fecha) => {
        if (!fecha) return "";
        return new Date(fecha.replace("Z", "")).toLocaleString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const handleAgregarObservacion = async () => {
        if (!nuevaObservacion.trim() || !cotizacion?.idTicket) return;
        setEnviandoObservacion(true);
        try {
            await agregarSeguimientoTicket(user?.user?.ID, cotizacion.idTicket, nuevaObservacion.trim());

            setCotizacion?.((prev) => ({
                ...prev,
                seguimiento: [
                    ...(prev.seguimiento ?? []),
                    { mensaje: nuevaObservacion.trim(), fecha: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() },
                ],
            }));

            setNuevaObservacion("");
        } catch {
            // El botón vuelve a estar disponible; el usuario puede reintentar.
        } finally {
            setEnviandoObservacion(false);
        }
    };

    const campos = [
        {
            icon: "pi pi-car",
            colorClass: "info-general__icono--amber",
            label: "Unidad",
            valor: cotizacion.rutas?.tipoUnidadNombre || "—",
            valorAnterior: cambioUnidad ? cambioUnidad.anterior : null,
        },
        {
            icon: "pi pi-calendar",
            colorClass: "info-general__icono--green",
            label: "Fecha de creación",
            valor: formatearFecha(cotizacion.fecha),
            mostrarLabel: true,
        },
        {
            icon: "pi pi-calendar-clock",
            colorClass: "info-general__icono--blue",
            label: "Fecha tentativa",
            valor: formatearFecha(cotizacion.rutas?.fechaTentativa),
            mostrarLabel: true,
        },
        {
            icon: "pi pi-ticket",
            colorClass: "info-general__icono--purple",
            label: "Ticket relacionado",
            valor: cotizacion.idTicket ? `#${cotizacion.idTicket}` : "Sin ticket",
            esTicket: true,
        },
        {
            icon: "pi pi-user-edit",
            colorClass: "info-general__icono--gray",
            label: "Creado por",
            valor: cotizacion.usuarioNombre || `Usuario #${cotizacion.usuario}`,
        },
    ];

    return (
        <>
            {/* Un solo Tooltip global: se aplica a cualquier elemento con la clase .info-general__icono */}
            <Tooltip target=".info-general__icono" position="top" />

            <div className="info-general">
                <div className="info-general__grid">
                    {campos.map((campo, i) => (
                        <div key={i} className="info-general__campo">
                            <div
                                className={`info-general__icono ${campo.colorClass}`}
                                data-pr-tooltip={campo.label}
                            >
                                <i className={campo.icon}/>
                            </div>
                            <div className="info-general__texto">
                              {/* Las dos fechas se ven iguales; la etiqueta visible evita confundirlas */}
                              {campo.mostrarLabel && <span className="info-general__label">{campo.label}</span>}
                              <span className="info-general__valor" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  {campo.valorAnterior && (
                                      <span style={{
                                          fontSize: "0.72rem",
                                          color: "var(--red-400)",
                                          textDecoration: "line-through",
                                          opacity: 0.75,
                                      }}>
                                          {campo.valorAnterior}
                                      </span>
                                  )}

                                  {campo.valorAnterior && (
                                      <i className="pi pi-arrow-right" style={{ fontSize: "0.65rem", color: "var(--text-color-secondary)" }} />
                                  )}

                                  {campo.valor}
                                  {/* Botón ojo solo en el campo de ticket */}
                                  {campo.esTicket && cotizacion && (
                                      <Button
                                          onClick={() => setShowTicketDetail(true)}
                                          icon="pi pi-eye"
                                          size="small"
                                          rounded outlined
                                          tooltip="Ver detalle del ticket"
                                      >
                                      </Button>
                                  )}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Dialog
                header={`Ticket #${cotizacion.idTicket}`}
                visible={showTicketDetail}
                style={{ width: "460px" }}
                modal
                onHide={() => setShowTicketDetail(false)}
            >
                {cotizacion && (
                    <div className="flex flex-column gap-3">
                        <div className="flex align-items-center gap-2">
                            <span className="text-sm text-color-secondary">Categoría:</span>
                            <Tag
                                value={cotizacion.categoria}
                                severity={cotizacion.categoria === "NUEVA" ? "success" : "warning"}
                            />
                        </div>

                        <div className="ticket-dialog__bloque">
                            <span className="ticket-dialog__label">Asunto</span>
                            <p className="ticket-dialog__asunto">{cotizacion.asunto}</p>
                        </div>

                        {/* ── Hilo de seguimiento — canal compartido entre inplant y operaciones ── */}
                        <div className="ticket-hilo">
                            <span className="ticket-dialog__label">Seguimiento</span>
                            {cotizacion.seguimiento?.length > 0 ? (
                                <div className="ticket-hilo__lista mt-2">
                                    {cotizacion.seguimiento.map((s, i) => (
                                        <div key={i} className="ticket-hilo__mensaje">
                                            <p className="ticket-hilo__texto">{s.mensaje}</p>
                                            <span className="ticket-hilo__fecha">{formatearFechaHora(s.fecha)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-color-secondary mt-2 mb-0">Aún no hay observaciones en este ticket.</p>
                            )}

                            <div className="ticket-hilo__composer">
                                <InputTextarea
                                    value={nuevaObservacion}
                                    onChange={(e) => setNuevaObservacion(e.target.value)}
                                    rows={2}
                                    autoResize
                                    placeholder="Agregar una observación al ticket..."
                                    style={{ width: "100%", fontSize: "0.85rem" }}
                                />
                                <Button
                                    icon="pi pi-send"
                                    label="Agregar"
                                    className="p-button-sm mt-2"
                                    onClick={handleAgregarObservacion}
                                    loading={enviandoObservacion}
                                    disabled={!nuevaObservacion.trim()}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default TarjetaInfoGeneral;
